import { useMemo } from 'react';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import { DividendNextStepsContainer } from '@/pages/CompanyReport/components/Dividend';
import NextDividend from '../NextDividend';
import type { PayoutEntity } from '../DividendPayout';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import track from 'react-tracking';
import {
  COMPANY_CURRENT_PAYOUT,
  COMPANY_DIVIDEND_YIELD_AND_PAYMENT,
  COMPANY_FUTURE_PAYOUT,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import {
  DIVIDEND_PAYOUT_CURRENT,
  DIVIDEND_PAYOUT_FUTURE,
  DIVIDEND_STABILITY_GROWTH,
  DIVIDEND_YIELD,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import type { AnalysisCheck } from '@/pages/CompanyReport/redux/interface';
import loadable from '@loadable/component';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import DividendYieldVsMarketTable from '../DividendYieldVsMarketTable';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import { TableWrapper } from './styled';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useDividendsData } from '../StabilityAndGrowthOfPayments/hooks/useDividendsData';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import { pickDividendData } from './utils';
import { formatterCurrencyTruncated } from '@/utilities/format';
import type { DividendData } from '../StabilityAndGrowthOfPayments/hooks/types';
import useExperimentPeerCompanies from '@/pages/CompanyReport/components/SimilarCompanies/useExperimentPeerCompanies';
import StabilityAndGrowthOfPaymentsContainer from '../StabilityAndGrowthOfPayments';

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const DividendPayout = loadable(() => import('../DividendPayout'), {
  ssr: false,
});

const DividendYieldVsMarket = loadable(
  () => import('../DividendYieldVsMarket'),
  { ssr: false }
);

const StabilityAndGrowthOfPaymentsTable = loadable(
  () => import('../StabilityAndGrowthOfPaymentsTable'),
  { ssr: false }
);

const CriteriaChecks = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks'
    )
);
const KeyInformation = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/KeyInformation'
    )
);
const RecentSectionUpdates = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates'
    )
);

export interface Props {
  isFund: boolean;
  companyId: string;
  companyName: string;
  sectionNumber?: number;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  dividendYieldFuture3Y: number | null;
  dividendNextDate: number;
  dividendNextDateFormatted: string;
  showUpcomingDividendPayment: boolean;
  EPSFormatted: string;
  dividendNextPayDate: number;
  dividendNextPayDateFormatted: string;
  dividendCheck: AnalysisCheck[];
  industryName: string;
  marketName: string;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  dividendYieldFuture3YFormatted: string;
  currentDividendPayout: PayoutEntity[];
  currentDividendPayoutRatio: number;
  currentDividendPayoutRatioFormatted: string;
  futureDividendPayout: PayoutEntity[];
  futureDividendPayoutRatio: number;
  futureDividendPayoutRatioFormatted: string;
  cashDividendPayout: PayoutEntity[];
  cashDividendPayoutRatio: number;
  cashDividendPayoutRatioFormatted: string;
  viewMode: ViewMode;
}

const Dividend = ({
  isFund,
  EPSFormatted,
  companyId,
  companyName,
  sectionNumber,
  dividendYield,
  dividendYieldFuture3Y,
  marketDividendYieldP25,
  marketDividendYieldP75,
  industryDividendYield,
  dividendNextDate,
  dividendNextDateFormatted,
  dividendNextPayDate,
  dividendNextPayDateFormatted,
  showUpcomingDividendPayment,
  dividendCheck,
  industryName,
  marketName,
  dividendYieldFormatted,
  marketDividendYieldP25Formatted,
  marketDividendYieldP75Formatted,
  industryDividendYieldFormatted,
  dividendYieldFuture3YFormatted,
  currentDividendPayout,
  currentDividendPayoutRatio,
  currentDividendPayoutRatioFormatted,
  futureDividendPayout,
  futureDividendPayoutRatio,
  futureDividendPayoutRatioFormatted,
  cashDividendPayout,
  cashDividendPayoutRatio,
  cashDividendPayoutRatioFormatted,
  viewMode,
}: Props) => {
  const {
    dividendYieldDownloadUrl,
    currentPayoutDownloadUrl,
    futurePayoutDownloadUrl,
  } = useMemo(() => {
    return {
      dividendYieldDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.HistoricDividend
      ),
      currentPayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.CurrentDividendPayout
      ),
      futurePayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FutureDividendPayout
      ),
    };
  }, [companyId]);

  const canonicalUrl = useCompanyCanonicalUrl();
  const dateIn3Y = (new Date().getUTCFullYear() + 3).toString();
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const term = useGlossaryTerms();
  const { data: divPaymentDetailData, status } = useDividendsData({
    id: companyId,
  });
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const {
    IsDividendSignificant,
    IsDividendYieldTopTier,
    IsDividendStable,
    IsDividendGrowing,
    IsDividendCovered,
    IsDividendCoveredIn3Years,
    IsDividendCoveredByFreeCashFlow,
    IsBank,
  } = statements || {};
  const isBank = IsBank?.value === true;

  const mostRecentDividend =
    status === 'success'
      ? pickDividendData(divPaymentDetailData)
      : ({} as DividendData);
  const nextStep = useExperimentPeerCompanies({
    originalElement: <DividendNextStepsContainer companyId={companyId} />,
    section: 'dividend',
  });

  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="dividend-section">
      <SectionSchema section="dividend" />
      <CompanySectionTitle title="Dividend" />
      <Introduction
        scoreName="Dividend"
        criteriaChecks={
          <CriteriaChecks scoreName="Dividend" checks={dividendCheck} />
        }
        keyInformation={
          <KeyInformation
            keyMetrics={[
              {
                title: 'Dividend yield',
                value: dividendYieldFormatted,
                tooltip: term('current dividend yield'),
              },
              {
                title: 'Payout ratio',
                value: currentDividendPayoutRatioFormatted,
                tooltip: term('payout ratio'),
              },
            ]}
            metrics={[
              {
                title: 'Industry average yield',
                value: industryDividendYieldFormatted,
              },
              {
                title: 'Next dividend pay date',
                value: dividendNextPayDateFormatted,
              },
              {
                title: 'Ex dividend date',
                value: dividendNextDateFormatted,
                tooltip: term('ex dividend date'),
              },
              {
                title: 'Dividend per share',
                value: formatterCurrencyTruncated(
                  mostRecentDividend.annualizedDividend,
                  mostRecentDividend.currencyIsoCode || 'USD',
                  3
                ),
                tooltip: term('dividend per share'),
              },
              {
                title: 'Earnings per share',
                value: EPSFormatted,
                tooltip: term('earnings per share'),
              },
              {
                title: 'Dividend yield forecast in 3Y',
                value: dividendYieldFuture3YFormatted,
              },
            ]}
          />
        }
        recentSectionUpdates={
          <RecentSectionUpdates
            lookup={canonicalUrl}
            title="Recent dividend updates"
            filterByEventCategory="dividend"
          />
        }
      />
      {showUpcomingDividendPayment && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Upcoming Dividend Payment"
            trackingProps={{ subject: 'chart' }}
            body={
              <NextDividend
                dividendNextDate={dividendNextDate}
                dividendNextPayDate={dividendNextPayDate}
              />
            }
          />
          <hr />
        </>
      )}

      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.dividend"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>
      <PageAnchor type="sub-section-title" id={DIVIDEND_STABILITY_GROWTH} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="Stability and Growth of Payments"
        trackingProps={{ subject: 'chart' }}
        body={
          <ChartWrapper
            id="dividend-stability-and-growth-of-payments-chart"
            chart={
              <StabilityAndGrowthOfPaymentsContainer
                divPaymentDetailData={divPaymentDetailData}
              />
            }
            table={
              <TableWrapper>
                <StabilityAndGrowthOfPaymentsTable
                  viewMode={viewMode}
                  companyName={companyName}
                  companyId={companyId}
                />
              </TableWrapper>
            }
            feedbackLabel={COMPANY_DIVIDEND_YIELD_AND_PAYMENT}
            exportDarkUrl={dividendYieldDownloadUrl}
            isLoading={status === 'pending'}
            loadingText="Fetching dividends data"
            preRenderHeightSmDown={460}
            preRenderHeight={480}
          />
        }
        statements={
          <>
            <Statement
              title={IsDividendStable?.title}
              description={<Description statement={IsDividendStable} />}
              value={IsDividendStable?.value}
              statementId={CompanyStatements.dividendStatement3}
            />
            <Statement
              title={IsDividendGrowing?.title}
              description={<Description statement={IsDividendGrowing} />}
              value={IsDividendGrowing?.value}
              statementId={CompanyStatements.dividendStatement4}
            />
          </>
        }
      />
      <hr />
      <PageAnchor type="sub-section-title" id={DIVIDEND_YIELD} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="Dividend Yield vs Market"
        trackingProps={{ subject: 'chart' }}
        body={
          <ChartWrapper
            id="dividend-yield-vs-market-chart"
            chart={
              <DividendYieldVsMarket
                companyName={companyName}
                industryName={industryName}
                marketName={marketName}
                dividendYield={dividendYield}
                dividendYieldFuture={dividendYieldFuture3Y}
                marketDividendYieldP25={marketDividendYieldP25}
                marketDividendYieldP75={marketDividendYieldP75}
                industryDividendYield={industryDividendYield}
                dividendYieldFormatted={dividendYieldFormatted}
                marketDividendYieldP25Formatted={
                  marketDividendYieldP25Formatted
                }
                marketDividendYieldP75Formatted={
                  marketDividendYieldP75Formatted
                }
                industryDividendYieldFormatted={industryDividendYieldFormatted}
                dividendYieldFutureFormatted={dividendYieldFuture3YFormatted}
              />
            }
            table={<DividendYieldVsMarketTable />}
            preRenderHeight={318}
          />
        }
        statements={
          <>
            <Statement
              title={IsDividendSignificant?.title}
              description={<Description statement={IsDividendSignificant} />}
              value={IsDividendSignificant?.value}
              statementId={CompanyStatements.dividendStatement1}
            />
            <Statement
              title={IsDividendYieldTopTier?.title}
              description={<Description statement={IsDividendYieldTopTier} />}
              value={IsDividendYieldTopTier?.value}
              statementId={CompanyStatements.dividendStatement2}
            />
          </>
        }
      />
      <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_CURRENT} />
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={`${isBank ? 'Current' : 'Earnings'} Payout to Shareholders`}
        trackingProps={{ subject: 'chart' }}
        split={true}
        body={
          <ChartWrapper
            id="dividend-earnings-payout-chart"
            preRenderHeight="258px"
            chart={
              <DividendPayout
                data-cy-id="chart-payout-to-shareholders"
                data={currentDividendPayout}
                payoutType="Earnings"
                payoutRatio={currentDividendPayoutRatio}
                payoutRatioFormatted={currentDividendPayoutRatioFormatted}
                payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                when={isBank ? 'Current' : undefined}
              />
            }
            feedbackLabel={COMPANY_CURRENT_PAYOUT}
            exportDarkUrl={currentPayoutDownloadUrl}
          />
        }
        statements={
          <Statement
            title={IsDividendCovered?.title}
            description={<Description statement={IsDividendCovered} />}
            value={IsDividendCovered?.value}
            statementId={CompanyStatements.dividendStatement5}
          />
        }
      />
      <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_FUTURE} />
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={`${isBank ? 'Future' : 'Cash'} Payout to Shareholders`}
        trackingProps={{ subject: 'chart' }}
        split={true}
        body={
          <ChartWrapper
            id="dividend-cash-payout-chart"
            preRenderHeight="258px"
            chart={
              <DividendPayout
                data-cy-id="chart-payout-to-shareholders"
                data={isBank ? futureDividendPayout : cashDividendPayout}
                payoutType={isBank ? 'Earnings' : 'Cash flow'}
                payoutRatio={
                  isBank ? futureDividendPayoutRatio : cashDividendPayoutRatio
                }
                payoutRatioFormatted={
                  isBank
                    ? futureDividendPayoutRatioFormatted
                    : cashDividendPayoutRatioFormatted
                }
                payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                payoutRatioIsNegative={
                  isBank
                    ? undefined
                    : 'Paying a dividend with no free cash flows'
                }
                when={isBank ? dateIn3Y : undefined}
              />
            }
            feedbackLabel={COMPANY_FUTURE_PAYOUT}
            exportDarkUrl={isBank ? futurePayoutDownloadUrl : undefined}
          />
        }
        statements={
          isBank ? (
            <Statement
              title={IsDividendCoveredIn3Years?.title}
              description={
                <Description statement={IsDividendCoveredIn3Years} />
              }
              value={IsDividendCoveredIn3Years?.value}
              statementId={CompanyStatements.dividendStatement6}
            />
          ) : (
            <Statement
              title={IsDividendCoveredByFreeCashFlow?.title}
              description={
                <Description statement={IsDividendCoveredByFreeCashFlow} />
              }
              value={IsDividendCoveredByFreeCashFlow?.value}
              statementId={CompanyStatements.dividendStatement7}
            />
          )
        }
      />

      {isFund === false && (
        <>
          <hr />
          {nextStep}
        </>
      )}
    </ReportSection>
  );
};

export default track({
  type: 'track',
  subject: 'dividends',
  section: 'dividends',
})(Dividend) as typeof Dividend;
